import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createEditor, Descendant, Editor, Range, Transforms } from "slate";
import {
    Editable,
    ReactEditor,
    RenderElementProps,
    Slate,
    useFocused,
    useSelected,
    useSlateStatic,
    withReact,
} from "slate-react";
import classNames from "classnames";
import { CloseIcon, InfoIcon, PlusIcon } from "@icons";
import { VariableDefinition } from "@common/helpers/variable-field";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import inputStyles from "@design-system/Input/Input.module.scss";
import { InputRoot } from "@design-system/Input/InputParts";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { InputSize, toDsInputSize } from "@helpers/form";
import { Popover, PopoverContent } from "@design-system/Popover/Popover";
import {
    slateToTokenString,
    tokenStringToSlate,
} from "./variableTextSerialization";
import {
    filterVariables,
    getVariableTrigger,
    insertVariable,
    VARIABLE_TRIGGER,
    withVariables,
} from "./variableTextSlate";
import styles from "./VariableTextField.module.scss";
import { VariableTextChip } from "./VariableTextChip";

const INSERT_VARIABLE_TOOLTIP =
    "Insert a value that fills in automatically for each entity";

interface VariableTextFieldProps {
    label?: string;
    optional?: boolean;
    /** Shown as the label info tooltip. */
    description?: string;
    error?: React.ReactNode;
    /** Error copy renders only when this is set. Invalid chrome still follows `error`. */
    showErrorMessage?: boolean;
    required?: boolean;
    className?: string;
    labelRight?: React.ReactNode;
    size?: InputSize;
    /** Seeds the editor once on mount; later changes aren't synced. Remount via `key` to reset. */
    value: string;
    /** Serialized `{{token}}` string, emitted on every edit. */
    onChange: (value: string) => void;
    variables: VariableDefinition[];
    trigger?: string;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    autoFocus?: boolean;
    lineBreaks?: "single" | "multi";
}

const VariableChip = ({
    attributes,
    children,
    element,
    variableKey,
    label,
    canRemove,
}: Pick<RenderElementProps, "attributes" | "children"> & {
    element: RenderElementProps["element"];
    variableKey: string;
    label: string;
    canRemove: boolean;
}) => {
    const editor = useSlateStatic();
    const selected = useSelected();
    const focused = useFocused();

    const handleRemove = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const path = ReactEditor.findPath(editor, element);
        Transforms.removeNodes(editor, { at: path });
        ReactEditor.focus(editor);
    };

    return (
        <VariableTextChip
            {...attributes}
            contentEditable={false}
            className={classNames({
                [styles.variableSelected]: selected && focused,
            })}
            data-variable-key={variableKey}
            label={label}
        >
            {canRemove && (
                <button
                    type="button"
                    className={styles.variableRemove}
                    aria-label={`Remove ${label}`}
                    onMouseDown={handleRemove}
                >
                    <CloseIcon />
                </button>
            )}
            {children}
        </VariableTextChip>
    );
};

export const VariableTextField: React.FC<VariableTextFieldProps> = ({
    value,
    onChange,
    variables,
    trigger = VARIABLE_TRIGGER,
    placeholder,
    disabled = false,
    readOnly = false,
    autoFocus = false,
    lineBreaks = "single",
    size,
    label,
    optional = false,
    description,
    error,
    showErrorMessage,
    required,
    className,
    labelRight,
}) => {
    const editor = useMemo(() => withVariables(withReact(createEditor())), []);

    const variableByKey = useMemo(
        () => new Map(variables.map((variable) => [variable.key, variable])),
        [variables],
    );
    const initialValueRef = useRef<Descendant[]>(
        tokenStringToSlate(value, (key) => variableByKey.has(key)),
    );

    const containerRef = useRef<HTMLDivElement | null>(null);
    const popoverTargetRef = useRef<HTMLSpanElement | null>(null);
    const addButtonRef = useRef<HTMLSpanElement | null>(null);
    // Set when the picker is opened via the "+" button rather than a typed
    // trigger, so trigger-detection in handleChange doesn't immediately close it.
    const isManualOpenRef = useRef(false);

    const [pickerTarget, setPickerTarget] = useState<Range | null>(null);
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerIndex, setPickerIndex] = useState(0);
    const [isPickerAnchoredToButton, setIsPickerAnchoredToButton] =
        useState(false);

    const filteredVariables = useMemo(
        () => filterVariables(variables, pickerSearch),
        [variables, pickerSearch],
    );

    const isPickerOpen = !!pickerTarget && filteredVariables.length > 0;

    useEffect(() => {
        if (
            !isPickerOpen ||
            !pickerTarget ||
            !popoverTargetRef.current ||
            !containerRef.current
        ) {
            return;
        }
        try {
            const domRange = ReactEditor.toDOMRange(editor, pickerTarget);
            const rect = domRange.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            popoverTargetRef.current.style.top = `${rect.bottom - containerRect.top}px`;
            popoverTargetRef.current.style.left = `${rect.left - containerRect.left}px`;
        } catch {
            // The range can momentarily be out of sync with the DOM.
        }
    }, [editor, isPickerOpen, pickerTarget]);

    const handleChange = useCallback(
        (newValue: Descendant[]) => {
            const isContentChange = editor.operations.some(
                (operation) => operation.type !== "set_selection",
            );
            if (isContentChange) {
                onChange(slateToTokenString(newValue));
            }

            const nextTrigger = getVariableTrigger(editor, trigger);
            if (nextTrigger) {
                isManualOpenRef.current = false;
                setIsPickerAnchoredToButton(false);
                setPickerTarget(nextTrigger.target);
                setPickerSearch(nextTrigger.search);
                setPickerIndex(0);
                return;
            }
            if (isManualOpenRef.current) {
                return;
            }
            setPickerTarget(null);
        },
        [editor, onChange, trigger],
    );

    const closePicker = useCallback(() => {
        isManualOpenRef.current = false;
        setIsPickerAnchoredToButton(false);
        setPickerTarget(null);
    }, []);

    const selectVariable = useCallback(
        (variable: VariableDefinition) => {
            if (!pickerTarget) {
                return;
            }
            Transforms.select(editor, pickerTarget);
            insertVariable(editor, variable.key);
            closePicker();
            ReactEditor.focus(editor);
        },
        [editor, pickerTarget, closePicker],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!isPickerOpen) {
                if (lineBreaks === "single" && event.key === "Enter") {
                    event.preventDefault();
                }
                return;
            }
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    setPickerIndex((prev) =>
                        prev >= filteredVariables.length - 1 ? 0 : prev + 1,
                    );
                    return;
                case "ArrowUp":
                    event.preventDefault();
                    setPickerIndex((prev) =>
                        prev <= 0 ? filteredVariables.length - 1 : prev - 1,
                    );
                    return;
                case "Tab":
                case "Enter":
                    event.preventDefault();
                    selectVariable(filteredVariables[pickerIndex]);
                    return;
                case "Escape":
                    event.preventDefault();
                    // The drawer hosting this field also closes on Escape.
                    event.stopPropagation();
                    closePicker();
                    return;
            }
        },
        [
            isPickerOpen,
            filteredVariables,
            pickerIndex,
            selectVariable,
            closePicker,
            lineBreaks,
        ],
    );

    const isEditable = !disabled && !readOnly;

    const handleAddVariable = useCallback(() => {
        isManualOpenRef.current = true;
        // Read the caret before focusing: focusing an unfocused editor resets a
        // null selection to the document start, so capture it (or fall back to
        // the end) first, then restore it after focus.
        const point = editor.selection
            ? Range.end(editor.selection)
            : Editor.end(editor, []);
        const target: Range = { anchor: point, focus: point };
        Transforms.select(editor, target);
        ReactEditor.focus(editor);
        setIsPickerAnchoredToButton(true);
        setPickerTarget(target);
        setPickerSearch("");
        setPickerIndex(0);
    }, [editor]);

    const renderElement = useCallback(
        (props: RenderElementProps) => {
            if (props.element.type === "variable") {
                const { variableKey } = props.element;
                return (
                    <VariableChip
                        attributes={props.attributes}
                        element={props.element}
                        canRemove={isEditable}
                        variableKey={variableKey}
                        label={
                            variableByKey.get(variableKey)?.label ?? variableKey
                        }
                    >
                        {props.children}
                    </VariableChip>
                );
            }
            return <p {...props.attributes}>{props.children}</p>;
        },
        [variableByKey, isEditable],
    );

    const editorField = (
        <InputRoot
            ref={containerRef}
            size={toDsInputSize(size)}
            disabled={disabled}
            className={classNames(styles.editorRoot, {
                [styles.editorFieldWithAdd]: isEditable,
                [styles.editorFieldMultiline]: lineBreaks === "multi",
            })}
            data-variable-text-field=""
        >
            <Slate
                editor={editor}
                initialValue={initialValueRef.current}
                onChange={handleChange}
            >
                <Field.Control
                    required={required}
                    render={(controlProps) => (
                        <Editable
                            {...pickFieldControlAriaProps(controlProps)}
                            className={classNames(
                                inputStyles.control,
                                styles.editorField,
                            )}
                            renderElement={renderElement}
                            placeholder={placeholder}
                            readOnly={disabled || readOnly}
                            aria-disabled={disabled || undefined}
                            autoFocus={autoFocus}
                            onKeyDown={handleKeyDown}
                        />
                    )}
                />
            </Slate>
            {isEditable && (
                <span ref={addButtonRef} className={styles.addButton}>
                    <Tooltip content={INSERT_VARIABLE_TOOLTIP}>
                        <button
                            type="button"
                            data-insert-variable=""
                            className={styles.addButtonControl}
                            aria-label={INSERT_VARIABLE_TOOLTIP}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                handleAddVariable();
                            }}
                        >
                            <PlusIcon />
                        </button>
                    </Tooltip>
                </span>
            )}
            <span ref={popoverTargetRef} className={styles.popoverTarget} />
            <Popover
                open={isPickerOpen}
                onOpenChange={(next, details) => {
                    if (next) {
                        return;
                    }
                    // The "+" control sits outside the popup, so Base UI reports
                    // the click that opens the picker as an outside-press. That
                    // control drives the picker itself, so ignore its own press
                    // here (as Base UI already does for a Popover trigger).
                    const pressTarget = details?.event?.target;
                    if (
                        details?.reason === "outside-press" &&
                        pressTarget instanceof Element &&
                        pressTarget.closest("[data-insert-variable]")
                    ) {
                        return;
                    }
                    closePicker();
                }}
            >
                <PopoverContent
                    align={isPickerAnchoredToButton ? "end" : "start"}
                    side="bottom"
                    sideOffset={4}
                    anchor={
                        isPickerAnchoredToButton
                            ? addButtonRef
                            : popoverTargetRef
                    }
                    initialFocus={false}
                    className={styles.picker}
                >
                    <div className={styles.pickerBody}>
                        {filteredVariables.map((variable, index) => (
                            <div
                                key={variable.key}
                                className={classNames(styles.option, {
                                    [styles.optionSelected]:
                                        index === pickerIndex,
                                })}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    selectVariable(variable);
                                }}
                                onMouseEnter={() => setPickerIndex(index)}
                            >
                                <span className={styles.optionLabel}>
                                    {variable.label}
                                </span>
                                <span className={styles.optionDescription}>
                                    {variable.description}
                                </span>
                            </div>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </InputRoot>
    );

    const errorMessage = showErrorMessage ? error : undefined;

    return (
        <Field.Root className={className} invalid={Boolean(error)}>
            {label || labelRight || description ? (
                <div className={styles.labelRow}>
                    <div className={styles.labelGroup}>
                        {label ? (
                            <Field.Label>
                                <span>{label}</span>
                                {optional ? (
                                    <span className={styles.optional}>
                                        (optional)
                                    </span>
                                ) : null}
                            </Field.Label>
                        ) : null}
                        {description ? (
                            <Tooltip content={description}>
                                <button
                                    type="button"
                                    aria-label="More information"
                                    className={styles.labelTooltip}
                                >
                                    <InfoIcon aria-hidden />
                                </button>
                            </Tooltip>
                        ) : null}
                    </div>
                    {labelRight}
                </div>
            ) : null}
            {editorField}
            {errorMessage ? (
                <Field.Error match>{errorMessage}</Field.Error>
            ) : null}
        </Field.Root>
    );
};
